import React, { useMemo, useState } from "react";
import { alpha } from "@mui/material/styles";
import { Helmet } from "react-helmet-async";
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  LinearProgress,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import {
  CalendarMonth,
  Bolt,
  Euro,
  Shield,
  ShowChart,
  PieChartOutline,
  WarningAmber,
  LocalPhone,
  EmojiEvents,
  TrackChanges,
  AutoAwesome,
  ChatBubbleOutline,
} from "@mui/icons-material";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { useSelector } from "react-redux";
import { ramoData } from "./constants";
import DashboardFilters from "./components/DashboardFilters";
import { DashboardCard, SmallLabel } from "./components/DashboardCard";
import KpiCard from "./components/KpiCard";
import MonthlyTrendChart from "./components/MonthlyTrendChart";

function Default() {
  const theme = useTheme();
  const role = useSelector((state) => state.role.role);
  const isAdmin = ["supAdm", "adm"].includes(role);
  const [yearFilter, setYearFilter] = useState("Anno corrente");
  const [ramiFilter, setRamiFilter] = useState("Tutti i rami");

  const totalPolizze = useMemo(
    () => ramoData.reduce((acc, curr) => acc + curr.polizze, 0),
    []
  );

  return (
    <>
      <Helmet title="Default Dashboard" />

      <DashboardFilters
        yearFilter={yearFilter}
        onYearFilterChange={setYearFilter}
        ramiFilter={ramiFilter}
        onRamiFilterChange={setRamiFilter}
        theme={theme}
      />

      <Box
        sx={{
          mb: 4,
          display: "grid",
          gap: 6,
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, minmax(0, 1fr))",
            md: "repeat(3, minmax(0, 1fr))",
            lg: `repeat(${isAdmin ? 6 : 5}, minmax(0, 1fr))`,
          },
          "& > *": {
            display: "flex",
          },
        }}
      >
        <Box><KpiCard icon={<CalendarMonth />} value="23" label="Preventivi Totali" delta="+18.4%" /></Box>
        <Box><KpiCard icon={<Shield />} value="12" label="Polizze Emesse" subLabel="Conversione 52.2%" delta="+12.3%" /></Box>
        <Box><KpiCard icon={<Euro />} value="€ 4.9K" label="Premi Totali" delta="+11.7%" /></Box>
        <Box><KpiCard icon={<ShowChart />} value="€ 409" label="Premio Medio" delta="+4.1%" /></Box>
        <Box>
          <DashboardCard>
            <CardContent sx={{ p: 5, "&:last-child": { pb: 5 } }}>
              <Stack direction="row" alignItems="center" spacing={1.2} mb={1.5}>
                <Avatar sx={{ bgcolor: alpha("#8b5cf6", 0.12), color: "#7c3aed", width: 40, height: 40 }}>
                  <PieChartOutline />
                </Avatar>
                <SmallLabel variant="body1">Emissioni per Ramo</SmallLabel>
              </Stack>
              <Stack spacing={0.8}>
                <SmallLabel variant="body2">● RCA <b style={{ float: "right", color: theme.palette.text.primary }}>6</b></SmallLabel>
                <SmallLabel variant="body2">● Incendio/Furto <b style={{ float: "right", color: theme.palette.text.primary }}>2</b></SmallLabel>
                <SmallLabel variant="body2">● Kasko <b style={{ float: "right", color: theme.palette.text.primary }}>0</b></SmallLabel>
                <SmallLabel variant="body2">● Assistenza <b style={{ float: "right", color: theme.palette.text.primary }}>1</b></SmallLabel>
              </Stack>
            </CardContent>
          </DashboardCard>
        </Box>
        {isAdmin && (
          <Box>
            <DashboardCard>
              <CardContent sx={{ p: 5, "&:last-child": { pb: 5 } }}>
                <Stack direction="row" justifyContent="space-between" mb={1.5}>
                  <Stack direction="row" alignItems="center" spacing={1.2}>
                    <Avatar sx={{ bgcolor: alpha("#f97316", 0.12), color: "#ea580c", width: 40, height: 40 }}>
                      <Bolt />
                    </Avatar>
                    <SmallLabel variant="body1">Highlights</SmallLabel>
                  </Stack>
                  <Typography variant="caption" color="primary" fontWeight={700}>LIVE</Typography>
                </Stack>
                <Typography variant="body2" sx={{ mb: 0.75 }}>🏆 <b>Top:</b> Mario Rossi - 4 polizze</Typography>
                <Typography variant="body2">📈 <b>Ramo top:</b> RCA</Typography>
              </CardContent>
            </DashboardCard>
          </Box>
        )}
      </Box>

      <Grid
        container
        spacing={6}
        mb={6}
        sx={{
          alignItems: "stretch",
          "& > .MuiGrid-item": {
            display: "flex",
          },
        }}
      >
        <Grid item xs={12} lg={8.8}>
          <DashboardCard>
            <CardContent sx={{ p: 6, "&:last-child": { pb: 6 } }}>
              <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ md: "center" }} mb={2.5} gap={1.5}>
                <Box>
                  <Typography variant="h4" fontWeight={800}>Andamento Mensile</Typography>
                  <Typography color="text.secondary">Preventivi, polizze e premi nel tempo</Typography>
                </Box>
              </Stack>

              <MonthlyTrendChart theme={theme} />
            </CardContent>
          </DashboardCard>
        </Grid>
        <Grid item xs={12} lg={3.2}>
          <Stack spacing={6} height="100%" width="100%">
            <DashboardCard>
              <CardContent sx={{ p: 6, "&:last-child": { pb: 6 } }}>
                <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                  <TrackChanges color="primary" fontSize="small" />
                  <Typography variant="h5" fontWeight={800}>Mobility Score</Typography>
                </Stack>
                <Box sx={{ position: "relative", display: "inline-flex", mx: "auto", width: "100%", justifyContent: "center", mb: 2 }}>
                  <CircularProgress variant="determinate" value={53} size={116} thickness={4} sx={{ color: "#f97316" }} />
                  <Box sx={{ top: 0, left: 0, bottom: 0, right: 0, position: "absolute", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column" }}>
                    <Typography variant="h3" fontWeight={800} color="#f97316">53</Typography>
                    <Typography variant="caption" color="text.secondary">/100</Typography>
                  </Box>
                </Box>
                <Stack spacing={0.9}>
                  <SmallLabel variant="body2">Produzione <span style={{ float: "right", color: "#ef4444", fontWeight: 700 }}>Bassa</span></SmallLabel>
                  <SmallLabel variant="body2">Conversione <span style={{ float: "right", color: "#16a34a", fontWeight: 700 }}>Alta</span></SmallLabel>
                  <SmallLabel variant="body2">Premio Medio <span style={{ float: "right", color: "#f97316", fontWeight: 700 }}>Media</span></SmallLabel>
                  <SmallLabel variant="body2">Cross-selling <span style={{ float: "right", color: "#f97316", fontWeight: 700 }}>Migliorabile</span></SmallLabel>
                </Stack>
              </CardContent>
            </DashboardCard>

            <DashboardCard>
              <CardContent sx={{ p: 6, "&:last-child": { pb: 6 } }}>
                <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                  <TrackChanges color="primary" fontSize="small" />
                  <Typography variant="h5" fontWeight={800}>Target Mensile</Typography>
                </Stack>
                <Box mb={2}>
                  <Stack direction="row" justifyContent="space-between" mb={0.5}>
                    <SmallLabel variant="body2">Polizze emesse</SmallLabel>
                    <Typography fontWeight={700} color="error.main">0%</Typography>
                  </Stack>
                  <Typography variant="h4" fontWeight={800} mb={1}>12 <Typography component="span" variant="h5" color="text.secondary">/ 6000</Typography></Typography>
                  <LinearProgress variant="determinate" value={0.2} sx={{ height: 12, borderRadius: 20 }} />
                </Box>
                <Box>
                  <Stack direction="row" justifyContent="space-between" mb={0.5}>
                    <SmallLabel variant="body2">Volume premi</SmallLabel>
                    <Typography fontWeight={700} color="error.main">0%</Typography>
                  </Stack>
                  <Typography variant="h4" fontWeight={800} mb={1}>€ 4.9K <Typography component="span" variant="h5" color="text.secondary">/ € 3.20M</Typography></Typography>
                  <LinearProgress variant="determinate" value={0.15} sx={{ height: 12, borderRadius: 20 }} />
                </Box>
              </CardContent>
            </DashboardCard>
          </Stack>
        </Grid>
      </Grid>

      <Grid
        container
        spacing={6}
        mb={6}
        sx={{
          alignItems: "stretch",
          "& > .MuiGrid-item": {
            display: "flex",
          },
        }}
      >
        <Grid item xs={12}>
          <DashboardCard>
            <CardContent sx={{ p: 6, "&:last-child": { pb: 6 } }}>
              <Typography variant="h4" fontWeight={800}>Distribuzione per Ramo</Typography>
              <Typography color="text.secondary" mb={2}>Analisi delle performance per tipologia prodotto</Typography>

              <Grid container spacing={6} alignItems="center">
                <Grid item xs={12} md={4}>
                  <Box sx={{ height: 320, position: "relative" }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={ramoData} dataKey="polizze" nameKey="ramo" cx="50%" cy="50%" innerRadius={78} outerRadius={124} paddingAngle={2}>
                          {ramoData.map((entry) => (
                            <Cell key={entry.ramo} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                    <Box
                      sx={{
                        position: "absolute",
                        inset: 0,
                        pointerEvents: "none",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Typography variant="h3" fontWeight={800}>{totalPolizze}</Typography>
                      <Typography color="text.secondary">Polizze Totali</Typography>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      mt: 3,
                      display: "flex",
                      flexWrap: "wrap",
                      justifyContent: "center",
                      gap: 1.25,
                    }}
                  >
                    {ramoData.map((entry) => (
                      <Chip
                        key={`legend-${entry.ramo}`}
                        size="small"
                        label={entry.ramo}
                        sx={{
                          borderRadius: 2,
                          border: `1px solid ${alpha(entry.color, 0.35)}`,
                          bgcolor: alpha(entry.color, 0.1),
                          color: theme.palette.text.primary,
                          "& .MuiChip-label": { fontWeight: 600 },
                        }}
                      />
                    ))}
                  </Box>
                </Grid>

                <Grid item xs={12} md={8}>
                  <Box sx={{ width: "100%", overflowX: "auto" }}>
                    <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", minWidth: 640 }}>
                      <Box component="thead">
                        <Box component="tr" sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
                          {[
                            "RAMO",
                            "PREV.",
                            "POLIZZE",
                            "CONV. %",
                            "PREMI",
                            "P. MEDIO",
                          ].map((head) => (
                            <Box key={head} component="th" sx={{ textAlign: head === "RAMO" ? "left" : "right", py: 1.25, color: theme.palette.text.secondary, fontWeight: 700, fontSize: 13 }}>{head}</Box>
                          ))}
                        </Box>
                      </Box>
                      <Box component="tbody">
                        {ramoData.map((row) => (
                          <Box key={row.ramo} component="tr" sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
                            <Box component="td" sx={{ py: 1.6, fontWeight: 700 }}>
                              <Box component="span" sx={{ color: row.color }}>●</Box> {row.ramo}
                            </Box>
                            <Box component="td" sx={{ py: 1.6, textAlign: "right" }}>{row.prev}</Box>
                            <Box component="td" sx={{ py: 1.6, textAlign: "right", fontWeight: 700 }}>{row.polizze}</Box>
                            <Box component="td" sx={{ py: 1.6, textAlign: "right", color: row.conv === "0.0%" ? "#ef4444" : "#16a34a", fontWeight: 700 }}>{row.conv}</Box>
                            <Box component="td" sx={{ py: 1.6, textAlign: "right" }}>{row.premi}</Box>
                            <Box component="td" sx={{ py: 1.6, textAlign: "right" }}>{row.medio}</Box>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </DashboardCard>
        </Grid>
      </Grid>

      <Grid
        container
        spacing={6}
        mb={6}
        sx={{
          alignItems: "stretch",
          "& > .MuiGrid-item": {
            display: "flex",
          },
        }}
      >
        <Grid item xs={12} lg={isAdmin ? 4 : 6}>
          <DashboardCard>
            <CardContent sx={{ p: 6, "&:last-child": { pb: 6 } }}>
              <Typography variant="h4" fontWeight={800}>Funnel Commerciale</Typography>
              <Typography color="text.secondary" mb={2}>Dal preventivo all'emissione</Typography>
              {[
                { label: "Preventivi Avviati", v: 23, p: 100, color: "#3b82f6" },
                { label: "Completati", v: 19, p: 83, color: "#8b5cf6" },
                { label: "Confermati", v: 15, p: 79, color: "#f97316" },
                { label: "Polizze Emesse", v: 12, p: 80, color: "#22c55e" },
              ].map((row) => (
                <Box key={row.label} mb={2}>
                  <Stack direction="row" justifyContent="space-between" mb={0.5}>
                    <Typography fontWeight={700}>{row.label}</Typography>
                    <Typography fontWeight={800}>{row.v} {row.label !== "Preventivi Avviati" ? <Chip size="small" label={`${row.p}%`} sx={{ ml: 0.8, height: 22 }} /> : null}</Typography>
                  </Stack>
                  <LinearProgress variant="determinate" value={row.p} sx={{ height: 16, borderRadius: 20, backgroundColor: alpha(theme.palette.text.primary, 0.08), "& .MuiLinearProgress-bar": { backgroundColor: row.color, borderRadius: 20 } }} />
                </Box>
              ))}
            </CardContent>
          </DashboardCard>
        </Grid>

        <Grid item xs={12} lg={isAdmin ? 4 : 6}>
          <DashboardCard>
            <CardContent sx={{ p: 6, "&:last-child": { pb: 6 } }}>
              <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                <CalendarMonth color="primary" />
                <Typography variant="h4" fontWeight={800}>Scadenze & Rinnovi</Typography>
              </Stack>
              <Typography color="text.secondary" mb={2}>Attività in scadenza da gestire</Typography>
              <Stack spacing={2.2}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar sx={{ bgcolor: alpha("#ef4444", 0.12), color: "#ef4444" }}><WarningAmber /></Avatar>
                  <Box>
                    <SmallLabel variant="body1">Scadenze 7 giorni</SmallLabel>
                    <Typography variant="h3" fontWeight={800}>2</Typography>
                  </Box>
                </Stack>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar sx={{ bgcolor: alpha("#f97316", 0.12), color: "#f97316" }}><CalendarMonth /></Avatar>
                  <Box>
                    <SmallLabel variant="body1">Scadenze 30 giorni</SmallLabel>
                    <Typography variant="h3" fontWeight={800}>6</Typography>
                  </Box>
                </Stack>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar sx={{ bgcolor: alpha("#3b82f6", 0.12), color: "#3b82f6" }}><LocalPhone /></Avatar>
                  <Box>
                    <SmallLabel variant="body1">Da ricontattare</SmallLabel>
                    <Typography variant="h3" fontWeight={800}>9</Typography>
                  </Box>
                </Stack>
              </Stack>
            </CardContent>
          </DashboardCard>
        </Grid>

        {isAdmin && (
          <Grid item xs={12} lg={4}>
            <DashboardCard>
              <CardContent sx={{ p: 6, "&:last-child": { pb: 6 } }}>
                <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                  <EmojiEvents sx={{ color: "#f97316" }} />
                  <Typography variant="h4" fontWeight={800}>Top Collaboratori</Typography>
                </Stack>
                <Typography color="text.secondary" mb={2}>Classifica per volume premi</Typography>
                {[
                  { name: "Mario Rossi", pol: "4 pol.", val: "€ 2.2K", p: 100, rank: "🥇" },
                  { name: "Giuseppe Verdi", pol: "2 pol.", val: "€ 970", p: 45, rank: "🥈" },
                  { name: "Sofia Russo", pol: "3 pol.", val: "€ 690", p: 31, rank: "🥉" },
                  { name: "Anna Colombo", pol: "1 pol.", val: "€ 490", p: 24, rank: "4°" },
                  { name: "Laura Bianchi", pol: "1 pol.", val: "€ 420", p: 20, rank: "5°" },
                ].map((row) => (
                  <Box key={row.name} mb={1.8}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.6}>
                      <Typography fontWeight={700}>{row.rank} {row.name}</Typography>
                      <Typography color="text.secondary">{row.pol}</Typography>
                    </Stack>
                    <LinearProgress variant="determinate" value={row.p} sx={{ height: 10, borderRadius: 20, backgroundColor: alpha(theme.palette.text.primary, 0.08), "& .MuiLinearProgress-bar": { borderRadius: 20, backgroundColor: "#2563eb" } }} />
                    <Typography textAlign="right" mt={0.4} fontWeight={800}>{row.val}</Typography>
                  </Box>
                ))}
              </CardContent>
            </DashboardCard>
          </Grid>
        )}
      </Grid>

      <Card
        sx={{
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: "none",
          height: "fit-content",
          minHeight: "unset",
          display: "block",
          flex: "0 0 auto",
          background:
            theme.palette.mode === "dark"
              ? "linear-gradient(90deg, rgba(37,99,235,0.06) 0%, rgba(17,24,39,0.36) 50%, rgba(16,185,129,0.06) 100%)"
              : "linear-gradient(90deg, rgba(37,99,235,0.025) 0%, rgba(248,250,252,0.95) 50%, rgba(16,185,129,0.025) 100%)",
        }}
      >
        <CardContent sx={{ p: 5, "&:last-child": { pb: 5 } }}>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={8.5}>
              <Stack direction="row" spacing={1.25} alignItems="center" mb={0.75}>
                <Avatar sx={{ bgcolor: alpha("#2563eb", 0.1), color: "#2563eb", width: 38, height: 38 }}><AutoAwesome fontSize="small" /></Avatar>
                <Box>
                  <Typography variant="h5" fontWeight={800}>Focus del giorno</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 0.8 }}>SUGGERIMENTO INTELLIGENTE</Typography>
                </Box>
              </Stack>
              <Typography variant="body1" sx={{ lineHeight: 1.55, pr: { md: 2 } }}>
                Il ramo Tutela Legale ha una conversione del 100%, superiore alla media. Ottimo momento per spingere il bundle con altri prodotti.
              </Typography>
            </Grid>
            <Grid item xs={12} md={3.5}>
              <Box sx={{ borderRadius: 2.5, border: `1px solid ${theme.palette.divider}`, bgcolor: alpha(theme.palette.background.paper, 0.85), p: 4 }}>
                <Stack direction="row" spacing={1} alignItems="center" mb={0.75}>
                  <ChatBubbleOutline color="primary" fontSize="small" />
                  <Typography variant="h6" fontWeight={800}>Ciao!</Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary" fontWeight={600}>
                  Oggi hai <b style={{ color: theme.palette.text.primary }}>12 pratiche</b> da lavorare e la tua rete ha generato <b style={{ color: theme.palette.text.primary }}>23 preventivi</b>.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  );
}

export default Default;
